import type {
  ConditionGroup,
  ConditionGroupOperator,
  RuleCondition,
} from "@redotech/redo-flows-common/rules/schemas";
import {
  QueryOperator,
  RuleConditionType,
} from "@redotech/redo-flows-common/rules/types";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback } from "react";
import { ConditionList } from "./condition-list";

export interface ConditionGroupEditorProps {
  group: ConditionGroup;
  onChange: (group: ConditionGroup) => void;
}

export const ConditionGroupEditor = memo(function ConditionGroupEditor({
  group,
  onChange,
}: ConditionGroupEditorProps) {
  const { t } = useTranslation();
  const operatorOptions: {
    id: string;
    type: "text";
    text: string;
    value: ConditionGroupOperator;
  }[] = [
    {
      id: "AND",
      type: "text",
      text: t("All", { context: "match every condition in the group" }),
      value: "AND",
    },
    {
      id: "OR",
      type: "text",
      text: t("Any", { context: "match at least one condition in the group" }),
      value: "OR",
    },
  ];

  const handleOperatorChange = useCallback(
    (operator: ConditionGroupOperator) => {
      onChange({ ...group, operator });
    },
    [group, onChange],
  );

  const handleConditionsChange = useCallback(
    (conditions: RuleCondition[]) => {
      onChange({ ...group, conditions });
    },
    [group, onChange],
  );

  const handleAddCondition = useCallback(() => {
    const newCondition: RuleCondition = {
      conditionType: RuleConditionType.TICKET_CHANNEL,
      channels: [],
      queryOperator: QueryOperator.IS,
    };
    onChange({ ...group, conditions: [...group.conditions, newCondition] });
  }, [group, onChange]);

  return (
    <Flex bgColor="primary" borderRadius="md" dir="column" gap="md" p="md">
      <Flex align="center" gap="sm">
        <RedoSingleSelectDropdownInput
          options={operatorOptions}
          optionSelected={({ item }) => handleOperatorChange(item.value)}
          selectedItem={operatorOptions.find(
            (opt) => opt.value === group.operator,
          )}
        />
        <Text fontSize="sm" textColor="secondary">
          {t("of these conditions must match")}
        </Text>
      </Flex>

      <Text fontSize="sm" fontWeight="medium">
        {t("Conditions")}
      </Text>

      <ConditionList
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        conditions={group.conditions as RuleCondition[]}
        onChange={handleConditionsChange}
        operator={group.operator}
      />

      <Flex gap="sm">
        <RedoButton
          hierarchy="tertiary"
          onClick={handleAddCondition}
          size="sm"
          text={t("+ Condition", {
            context: "button label; keep the leading plus sign",
          })}
        />
      </Flex>
    </Flex>
  );
});
